import {
  createContext,
  useContext,
  useState,
  useEffect,
} from "react";

import Swal from "sweetalert2";
import { flyToCart } from "../../utils/flyToCart";
import api from "../../api";
import { useModal } from "../../ModalContext";
import { CgDanger } from "react-icons/cg";

const CartContext = createContext();

/**
 * Normalize cart data coming from localStorage.
 *
 * This:
 * - converts IDs to numbers
 * - converts qty/maxQty to numbers
 * - merges duplicate products
 * - prevents quantity from exceeding maxQty
 */
const normalizeCart = (items) => {
  if (!Array.isArray(items)) return [];

  const merged = [];

  items.forEach((item) => {
    const id = Number(item.id);

    // Ignore invalid products
    if (!Number.isFinite(id)) {
      return;
    }

    const qty = Math.max(1, Number(item.qty) || 1);
    const maxQty = Math.max(0, Number(item.maxQty) || 0);

    const existing = merged.find(
      (x) => Number(x.id) === id
    );

    if (existing) {
      const combinedQty =
        Number(existing.qty) + qty;

      existing.qty =
        maxQty > 0
          ? Math.min(combinedQty, maxQty)
          : combinedQty;

      return;
    }

    merged.push({
      ...item,
      id,
      qty:
        maxQty > 0
          ? Math.min(qty, maxQty)
          : qty,
      maxQty,
    });
  });

  return merged;
};

export const CartProvider = ({ children }) => {
  const { openModal } = useModal();

  /**
   * Load cart from localStorage
   *
   * normalizeCart() also fixes old Safari cart data
   * that may already contain duplicate products.
   */
  const [cartItems, setCartItems] = useState(() => {
    try {
      const stored =
        localStorage.getItem("cartItems");

      if (!stored) {
        return [];
      }

      const parsed = JSON.parse(stored);

      return normalizeCart(parsed);
    } catch (error) {
      console.error(
        "Failed to load cart from localStorage:",
        error
      );

      return [];
    }
  });

  const [termsAccepted, setTermsAccepted] =
    useState(false);

  /**
   * Save cart whenever it changes.
   */
  useEffect(() => {
    try {
      localStorage.setItem(
        "cartItems",
        JSON.stringify(cartItems)
      );
    } catch (error) {
      console.error(
        "Failed to save cart to localStorage:",
        error
      );
    }
  }, [cartItems]);

  const clearCart = () => {
    setCartItems([]);
    localStorage.removeItem("cartItems");
  };

  /**
   * ADD TO CART
   */
  const addToCart = async (
    item,
    qty = 1,
    sourceEl = null
  ) => {
    /**
     * IMPORTANT:
     * This is currently a test IP.
     *
     * Remove it later if your backend automatically
     * detects the real client IP.
     */
    const testIp = "178.135.244.151";

    const productId = Number(
      item.id ?? item._id
    );

    /**
     * Product ID must come from database.
     */
    if (!Number.isFinite(productId)) {
      console.error(
        "Invalid product ID:",
        item.id,
        item._id
      );

      openModal(
        <CgDanger
          size={40}
          color="#993400"
        />,
        "Error",
        "Invalid product information."
      );

      return;
    }

    /**
     * 1. Check geographical restriction
     */
    try {
      const res = await api.post(
        "zone/check-zone",
        {
          productId,
          clientIp: testIp,
        }
      );

      const data = res.data;

      if (!data.ok) {
        openModal(
          <CgDanger
            size={40}
            color="#993400"
          />,
          "Not Available in Your Region",
          data.message ||
          "This product cannot be purchased in your area."
        );

        return;
      }
    } catch (err) {
      console.error(
        "Zone check failed:",
        err
      );

      openModal(
        <CgDanger
          size={40}
          color="#993400"
        />,
        "Error",
        "Something went wrong while checking your region."
      );

      return;
    }

    /**
     * 2. Prepare cart item
     */
    const requestedQty = Math.max(
      1,
      Number(qty) || 1
    );

    const maxQty = Math.max(
      0,
      Number(item.qty) || 0
    );

    const cartItem = {
      id: productId,
      title: item.titleEn,
      price: Number(item.price) || 0,
      image: `https://api.righttrack.com.lb/assets/images/${item.cover_front}`,
      qty: requestedQty,
      maxQty,
      format: item.format,
      nbofdiscs: item.nbofdiscs,
    };

    /**
     * Do not allow an item to be added if
     * there is no available stock.
     */
    // if (maxQty <= 0) {
    //   Swal.fire({
    //     icon: "warning",
    //     title: "Out of Stock",
    //     text: "This product is currently out of stock.",
    //     confirmButtonColor: "#000",
    //     customClass: {
    //       popup: "my-swal-popup",
    //     },
    //   });

    //   return;
    // }
    if (maxQty <= 0) {
      return false;
    }
    /**
     * Do not allow the requested quantity
     * to exceed stock when adding for first time.
     */
    if (requestedQty > maxQty) {
      return false;
    }

    /**
     * IMPORTANT:
     *
     * Check for existing product INSIDE
     * setCartItems(prev => ...)
     *
     * This prevents the race condition caused
     * by the async zone API request.
     */
    setCartItems((prev) => {
      const existing = prev.find(
        (x) =>
          Number(x.id) ===
          Number(cartItem.id)
      );

      /**
       * Product already exists:
       * increase its quantity.
       */
      if (existing) {
        const currentQty =
          Number(existing.qty) || 0;

        const newQty =
          currentQty +
          requestedQty;

        const currentMaxQty =
          Number(cartItem.maxQty);

        /**
         * Do not exceed available stock.
         */
        if (newQty > currentMaxQty) {
          return prev;
        }
        return prev.map((x) =>
          Number(x.id) ===
            Number(cartItem.id)
            ? {
              ...x,
              qty: newQty,

              // Refresh stock from current product
              maxQty: currentMaxQty,

              // Refresh these too in case
              // product data changed.
              price: cartItem.price,
              image: cartItem.image,
              format: cartItem.format,
              nbofdiscs:
                cartItem.nbofdiscs,
            }
            : x
        );
      }

      /**
       * Product does not exist:
       * add it as a new cart item.
       */
      return [
        cartItem,
        ...prev,
      ];
    });

    /**
     * Cart animation
     */
    if (sourceEl) {
      flyToCart(
        sourceEl,
        cartItem.image
      );
    }
  };

  /**
   * REMOVE ITEM COMPLETELY
   *
   * This does NOT decrease quantity.
   * It deletes the whole product from cart.
   */
  const removeItem = (id) => {
    const productId = Number(id);

    setCartItems((prev) =>
      prev.filter(
        (x) =>
          Number(x.id) !== productId
      )
    );
  };

  /**
   * INCREASE QUANTITY BY 1
   */
  const increaseQty = (id) => {
    const productId = Number(id);

    setCartItems((prev) =>
      prev.map((x) => {
        if (Number(x.id) !== productId) {
          return x;
        }

        const currentQty = Number(x.qty) || 0;
        const maxQty = Number(x.maxQty) || 0;

        // Already at maximum → do nothing
        if (currentQty >= maxQty) {
          return x;
        }

        return {
          ...x,
          qty: currentQty + 1,
        };
      })
    );
  };

  /**
   * DECREASE QUANTITY BY 1
   *
   * 3 -> 2
   * 2 -> 1
   * 1 -> removed
   */
  const decreaseQty = (id) => {
    const productId = Number(id);

    setCartItems((prev) =>
      prev
        .map((x) => {
          if (
            Number(x.id) !==
            productId
          ) {
            return x;
          }

          return {
            ...x,
            qty:
              Number(x.qty) - 1,
          };
        })
        .filter(
          (x) =>
            Number(x.qty) > 0
        )
    );
  };

  return (
    <CartContext.Provider
      value={{
        cartItems,
        addToCart,
        removeItem,
        increaseQty,
        decreaseQty,
        clearCart,
        termsAccepted,
        setTermsAccepted,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () =>
  useContext(CartContext);